import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { CardsComponent } from "@components/cards/cards.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { LabelComponent } from "@components/label/label.component";
import { MenuButtonComponent } from "@components/menu-button/menu-button.component";
import { MessagePanelComponent } from "@components/message-panel/message-panel.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { IMessageLabel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { AnvandareService } from "~app/common/services/anvandare.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommandManager } from "~app/common/services/cqs/commandManager";
import { EventService } from "~app/common/services/cqs/event.service";
import { EventHubService } from "~app/common/services/eventhub.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { SendViol3ArbetsorderCommand } from "~app/features/administration/systemadministration/tabs/sysadm-viol3/commands/SendViol3ArbetsorderCommand";
import { ArbetsorderVirkesorderService } from "~app/features/planera/services/arbetsorder-virkesorder.service";
import {
    ArbetsorderViol3ButtonComponent,
    ArbetsorderViol3ButtonComponent as ArbetsorderViol3ButtonComponent_1
} from "~app/features/shared/components/arbetsorder-viol3-button/arbetsorder-viol3-button.component";
import { ArbetsorderEventStore } from "~app/features/shared/services/arbetsorderEventStore.service";
import { VirkesorderService } from "~app/features/virkesorder/virkesorder.service";
import { ValidationMessageRowComponent } from "../../../../../common/validation/validation-message-row/validation-message-row.component";

import { ArbetsorderIntressentArbetsorderChangedMessage } from "../../events/ArbetsorderIntressentArbetsorderChangedMessage";
import { ArbetsorderIntressenterAddedEventMessage } from "../../events/ArbetsorderIntressenterAddedEventMessage";
import { TplTabOpenMessage } from "../../events/TplTabOpenMessage";
import { TplHandelseViewModel } from "../../models/TplHandelseViewModel";
import { RemoveArbetsorderIntressentCommand } from "../../models/commands";
import { ITplHandelser } from "../../models/dtos";
import { TplGetHandelserQuery } from "../../models/queries";
import { TplService } from "../../services/tpl.service";
import { TplTabId } from "../../state/state-models";
import { TplArbetsorderInfoModalComponent } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal.component";
import { TplHandelserRemoveAoWarningComponent } from "./tpl-handelser-remove-ao-warning/tpl-handelser-remove-ao-warning.component";

@Component({
    selector: "rask-tpl-handelser",
    templateUrl: "./tpl-handelser.component.html",
    styleUrls: ["./tpl-handelser.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        BusySpinnerComponent,
        MenuButtonComponent,
        ActionButtonComponent,
        CheckboxComponent,
        FormsModule,
        CardsComponent,
        MessagePanelComponent,
        LabelComponent,
        NgClass,
        RouterLink,
        ButtonComponent,
        ArbetsorderViol3ButtonComponent_1,
        ValidationMessageRowComponent,
        TranslatePipe
    ]
})
export class TplHandelserComponent implements OnInit, OnDestroy {
    readonly virkesorderService = inject(VirkesorderService);

    allSelected: boolean;
    canMasshantera = false;
    @ViewChildren("viol3Button") components: QueryList<ArbetsorderViol3ButtonComponent>;
    @Output() dirtychanged = new EventEmitter<number>();
    handelser: TplHandelseViewModel[];
    isLoading = true;
    messageLabels: IMessageLabel[] = [
        { severity: MessageSeverity.Warning,
            labelText: "Observera" }
    ];
    nrOfSelectedHandelser = 0;

    private readonly anvandareService = inject(AnvandareService);
    private readonly arbetsorderEventStore = inject(ArbetsorderEventStore);
    private readonly arbetsorderVirkesorderService = inject(ArbetsorderVirkesorderService);
    private readonly commandService = inject(CommandService);
    private readonly eventHubService = inject(EventHubService);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly tplService = inject(TplService);

    private userId: number;

    aoDetails($event, arbetsorderId: number) {
        $event.preventDefault();

        const handelse = this.handelser.find(x => x.arbetsorderId === arbetsorderId);
        TplArbetsorderInfoModalComponent.open({
            arbetsorderId: arbetsorderId,
            isSelected: handelse.selected,
            showSelected: true
        }, this.modalService, (result) => {
            handelse.selected = result.isSelected;
            this.updateSelectedView();
        });
    }

    canUppdateraAlla(): boolean {
        return this.handelser.some(handelse => !handelse.viol3 && handelse.canSendToViol()) || this.components.some(x => x.canSend());
    }

    getIkonVirkesorderstatus(handelse: TplHandelseViewModel): string {
        const icon = handelse.viol3 ? "fas fa-fw fa-file-alt" : "fas fa-fw fa-file";
        if (!handelse.viol3) {
            return `${icon} ${this.arbetsorderVirkesorderService.getIkonVirkesorderstatus(handelse.voStatus, handelse.isDirty, ObjectUtils.hasValue(handelse.skickad))}`;
        } else {
            return `${icon} ${this.arbetsorderVirkesorderService.getIkonForViol3(
                handelse.hasTransmissionErrorViol3, ObjectUtils.hasValue(handelse.skickad), handelse.dirtyProduktion, handelse.dirtyTransport)}`;
        }
    }

    ngOnDestroy(): void {
        this.unregisterListener();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.anvandareService.getCurrentUserId().subscribe((result: number) => {
            this.userId = result;
            this.registerListener();
        });
        this.initData();
    }

    onAllSelectedChanged($event: boolean) {
        this.allSelected = $event;
        this.handelser.forEach(x => {
            x.selected = $event;
        });
        this.updateSelectedView();
    }

    onCheckedChanged($event: any) {
        setTimeout(() => {
            this.updateSelectedView();
        }, 200);
    }

    sendToViol(handelse: TplHandelseViewModel) {
        if (!handelse.canSendToViol) {
            return;
        }
        this.sendListToViol([handelse]);
    }

    setSkickadDatum(skickad: Date) {
        return DateUtils.formatDateString(skickad, DateUtils.datetimeformatWithoutSeconds);
    }

    taBort() {
        const selectedHandelser = this.handelser.filter(h => h.isSelected);
        const selectedDirty = selectedHandelser.filter(h => h.isDirty);
        if (selectedDirty.length > 0) {
            const messageHeader = "Är du säker du vill ta bort händelser?";
            const aos = selectedDirty.map(h => h.arbetsorderBeteckning).join(", ");
            const message = `Virkesordern för arbetsorder ${aos} måste uppdateras. Vill du fortsätta med att ta bort från listan?`;

            TplHandelserRemoveAoWarningComponent.open(messageHeader, message, this.modalService, (res: boolean) => {
                if (res) {
                    this.removeArbetsorderIntressent(selectedHandelser);
                }
            });
        } else {
            this.removeArbetsorderIntressent(selectedHandelser);
        }
    }

    updateSelectedView() {
        this.setNrOfSelectedAos();
    }

    uppdateraAlla(): void {
        const handelser = this.handelser
            .filter(handelse => handelse.canSendToViol());
        this.sendListToViol(handelser);
    }

    private getNewHandelser(arbetsorderIds: number[]) {
        this.tplService.getHandelser(new TplGetHandelserQuery(arbetsorderIds)).subscribe((res: ITplHandelser) => {
            if (res?.arbetsordrar) {
                res.arbetsordrar.forEach(ao => {
                    const handelse = this.handelser.find(h => h.arbetsorderId === ao.arbetsorderId);
                    if (!ObjectUtils.hasValue(handelse)) {
                        this.handelser.push(new TplHandelseViewModel(ao, this.virkesorderService));
                        this.arbetsorderEventStore.addArbetsorderList([ao.arbetsorderId]);
                    }
                });
                this.onHandelserChanged();
            }
        });
    }

    private initData() {
        this.tplService.getHandelser(new TplGetHandelserQuery([])).subscribe((res: ITplHandelser) => {
            if (res?.arbetsordrar) {
                this.handelser = res.arbetsordrar.map(g => new TplHandelseViewModel(g, this.virkesorderService));
                this.sortHandelser();
                this.onHandelserChanged();
                this.arbetsorderEventStore.addArbetsorderList(this.handelser.map(x => x.arbetsorderId));
                this.arbetsorderEventStore.arbetsorderSynchFinished$.subscribe(x => {
                    if (x) {
                        this.updateHandelser([x.arbetsorderId]);
                    }
                });
            } else {
                this.handelser = [];
            }
            this.isLoading = false;
        });
    }

    private onHandelserChanged() {
        this.dirtychanged.emit(this.handelser.filter(x => x.isDirty).length);
    }

    private registerListener() {
        this.eventHubService.registerListener(`anvandare/${this.userId.toString()}`);

        this.eventService.subscribeEvent<ArbetsorderIntressentArbetsorderChangedMessage>(ArbetsorderIntressentArbetsorderChangedMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (event.anvandareId !== this.userId) {
                    return;
                }
                const handelse = this.handelser.find(x => x.arbetsorderId === event.arbetsorderId);
                if (!ObjectUtils.hasValue(handelse)) {
                    return;
                }
                this.updateHandelser([event.arbetsorderId]);
            });

        this.eventService.subscribeEvent<ArbetsorderIntressenterAddedEventMessage>(ArbetsorderIntressenterAddedEventMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (event.anvandareId !== this.userId) {
                    return;
                }

                this.getNewHandelser([event.arbetsorderId]);
            });

        this.eventService.subscribeEvent<TplTabOpenMessage>(TplTabOpenMessage.EVENTNAME)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event) => {
                if (event.tabId === TplTabId.handelser) {
                    this.sortHandelser();
                }
            });
    }

    private removeArbetsorderIntressent(handelser: TplHandelseViewModel[]) {
        this.arbetsorderEventStore.removeArbetsorderList(handelser.map(x => x.arbetsorderId));
        handelser.forEach(h => {
            this.handelser.splice(this.handelser.indexOf(h), 1);
        });

        this.onHandelserChanged();
        this.updateSelectedView();

        this.commandService.addTransactionCommand(new RemoveArbetsorderIntressentCommand(handelser.map(h => h.arbetsorderId)));
        this.commandService.pushCommandQueueTransaction();
    }

    private sendListToViol(handelser: TplHandelseViewModel[]): void {
        const cm = new CommandManager();
        handelser.forEach(handelse => {
            if (handelse.viol3) {
                cm.updateCommand(new SendViol3ArbetsorderCommand(handelse.arbetsorderId));
            }

            handelse.pending = true;
        });
        this.commandService.pushCommands(cm);
    }

    private setNrOfSelectedAos() {
        this.nrOfSelectedHandelser = this.handelser?.filter(x => x.isSelected).length ?? 0;
        this.canMasshantera = this.nrOfSelectedHandelser > 0;
        this.allSelected = this.nrOfSelectedHandelser > 0 && this.nrOfSelectedHandelser === (this.handelser?.length ?? 0);
    }

    /**
     * Skickad trumfar arbetsorderBeteckning
     * Händelse med fel/varning trumfar skickad
     */
    private sortHandelser(): void {
        const beteckningPredicate = (a, b) => {
            if (a.arbetsorderBeteckning > b.arbetsorderBeteckning) {
                return 1;
            }

            return a.arbetsorderBeteckning < b.arbetsorderBeteckning ? -1 : 0;
        };

        this.handelser.sort((a, b) => {
            if (a.hasWarningsOrErors || b.hasWarningsOrErors) {
                if (a.hasWarningsOrErors === b.hasWarningsOrErors) {
                    return beteckningPredicate(a, b);
                }

                return a.hasWarningsOrErors ? -1 : 1;
            }

            const canSendA = a.canSendToViol();
            const canSendB = b.canSendToViol();

            if (canSendA === canSendB) {
                return beteckningPredicate(a, b);
            }

            return canSendA ? -1 : 1;
        });
    }

    private unregisterListener() {
        this.eventHubService.unregisterListener(`anvandare/${this.userId.toString()}`);
    }

    private updateHandelser(arbetsorderIds: number[]) {
        this.tplService.getHandelser(new TplGetHandelserQuery(arbetsorderIds)).subscribe((res: ITplHandelser) => {
            if (res?.arbetsordrar) {
                res.arbetsordrar.forEach(x => {
                    const handelse = this.handelser.find(h => h.arbetsorderId === x.arbetsorderId);
                    if (handelse) {
                        handelse.updateModel(x);
                    }
                });
                this.onHandelserChanged();
            }
        });
    }
}
